/**
 * YourIssuesPage — the minimal "Your Issues" list the Report Feedback receipt
 * links to. SPEC-stakeholder-portal-2026-06-23, R3.
 *
 * This is the MINIMAL surface AC5 requires (a durable, non-black-hole receipt):
 * the reporter's own feedback, each with its claim-ladder rung — an untriaged
 * report shows as 📥 Received. The FULL gated stakeholder portal (roadmap, the
 * Resolved/Still-broken accept action, per-tenant visibility tiers) is R5; this
 * page deliberately shows only the own-reported-issues floor.
 *
 * Data comes from api.feedback.list() -> R1's tenant privacy read, so it can only
 * ever contain THIS tenant's own rows and never any internal-only field.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { YOUR_ISSUES_QUERY_KEY } from '@/components/feedback/ReportFeedbackButton';
import { useAuth } from '@/lib/auth/AuthProvider';
import { usePortalPanel } from '@/lib/portal/PortalPanelContext';
import type { FeedbackItem } from '@/lib/schemas';
import { StatusPill } from '@/components/portal/StatusPill';
import { clientPillLabel } from '@/lib/portal/clientRung';
import { Loader2 } from 'lucide-react';

// R1 AC6 / R3 AC6: the hardened header panel (My Submissions tab) is the ONE
// canonical status surface for a portal-enabled client. /your-issues is
// retired BEHIND the capability — a portal-enabled client is redirected to
// the dashboard with the panel OPENED (the retired /stakeholder-portal route
// no longer exists to navigate to); a client WITHOUT the capability is
// unaffected and still sees this minimal own-issues list.
const STAKEHOLDER_PORTAL_CAP = 'stakeholder_portal';

export function YourIssuesPage() {
  const { can } = useAuth();
  const { openPanel } = usePortalPanel();
  const portalEnabled = typeof can === 'function' && can(STAKEHOLDER_PORTAL_CAP);

  const { data, isLoading, isError } = useQuery({
    queryKey: YOUR_ISSUES_QUERY_KEY,
    queryFn: () => api.feedback.list(),
    enabled: !portalEnabled,
  });

  // R3 AC6: open the panel BEFORE navigating away — PortalPanelContext is
  // mounted above <Router>, so its state survives the redirect and the panel
  // is already flagged open the instant the dashboard's own
  // YourSubmissionsBell mounts (no dead loop, no dashboard-only redirect).
  useEffect(() => {
    if (portalEnabled) openPanel('my_submissions');
  }, [portalEnabled, openPanel]);

  if (portalEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  const items: FeedbackItem[] = data?.feedback ?? [];

  return (
    <DashboardLayout title="Your Issues">
      <div className="max-w-3xl">
        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your issues…
          </div>
        )}

        {isError && (
          <div className="text-destructive py-8">Could not load your issues. Please try again.</div>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <div className="text-muted-foreground py-8" data-testid="your-issues-empty">
            You have not reported anything yet. Use “Report Feedback” in the top bar to send us
            something and it will show up here.
          </div>
        )}

        {!isLoading && !isError && items.length > 0 && (
          <ul className="divide-y rounded-md border" data-testid="your-issues-list">
            {items.map((item) => (
              <li key={item.feedback_id} className="p-4" data-testid="your-issues-item">
                <div className="flex items-start justify-between gap-4">
                  <p className="text-sm whitespace-pre-wrap min-w-0">{item.body}</p>
                  <StatusPill
                    rung={item.claim_ladder}
                    label={clientPillLabel(item.claim_ladder.key)}
                    testId="your-issues-status"
                    className="shrink-0"
                  />
                </div>
                {item.route && (
                  <div className="mt-1 text-xs text-muted-foreground font-mono">{item.route}</div>
                )}
                {item.resolution_note && (
                  <div className="mt-2 text-sm text-muted-foreground">{item.resolution_note}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardLayout>
  );
}

export default YourIssuesPage;
